import type { Locale } from '@/i18n/locale'

/**
 * A flag for each language in the switcher.
 *
 * ## Inline SVG rather than a flag library
 *
 * Four icons do not justify a dependency, and the two obvious alternatives both
 * fail on this shop floor: an icon package ships two hundred flags to every
 * phone on plant wifi to use four, and Unicode regional-indicator pairs (🇧🇦)
 * **do not render as flags on Windows at all** — they fall back to the two
 * letters, and a good share of the terminals here are Windows desktops. Inline
 * paths render identically everywhere, work offline, and cost about a line each.
 *
 * ## What a flag beside a language does and does not mean
 *
 * Worth writing down, because the honest objection to flags here is not
 * technical. Bosnian, Croatian and Serbian are all spoken *in Bosnia*, by people
 * who live here — these are three languages of one country, not three
 * countries' languages, and a flag says the second thing. English has no flag
 * at all; the Union Jack below stands for the language by convention and for
 * nothing else.
 *
 * The two-letter code stays beside every flag for exactly that reason. It is
 * the ISO 639-1 *language* code, it is what the control is really indexing, and
 * it is what somebody reads if the flag says the wrong thing to them.
 *
 * ## Drawing them at 20×14
 *
 * At this size a coat of arms is three grey pixels, so each flag is reduced to
 * what still separates it from the others: Bosnia's diagonal and stars,
 * Croatia's chequy square, Serbia's band order — Serbia and Croatia share three
 * colours and differ only in their sequence, which is the one detail that has
 * to survive.
 *
 * ## Why the svg says `size-full` and the span carries the size
 *
 * Every place this is rendered — a `Button`, a `DropdownMenuItem` — carries the
 * shadcn rule `[&_svg:not([class*='size-'])]:size-4`, which exists so that a
 * bare lucide icon comes out at a sensible size. It is a blanket rule over
 * *any* descendant svg, and `size-4` is square, so it was squaring these:
 * measured in the language menu, a 20×14 flag was rendering at 15.2×15.2. Every
 * flag was squeezed a fifth narrower and stretched a tenth taller — enough that
 * Bosnia's diagonal came out at the wrong angle and its stars as ovals.
 *
 * The rule's own escape hatch is the `:not([class*='size-'])`, so a class with
 * `size-` in its name opts out. `size-full` is that class and is also exactly
 * what is wanted: the wrapper below is 20×14 and the flag fills it. Keep both
 * halves together — an svg here with no `size-` class silently becomes a square
 * again, and nothing fails except the picture.
 */
export function LocaleFlag({ locale, className }: { locale: Locale; className?: string }) {
  const Flag = FLAGS[locale]

  return (
    <span
      // The box lives here, on the wrapper, and the flag inside is told to fill
      // it — see the note above about `size-full`.
      //
      // A hairline keeps a white or pale edge from bleeding into the menu
      // background; without it Croatia's white band has no left edge at all.
      className={`inline-block h-3.5 w-5 overflow-hidden rounded-[2px] ring-1 ring-black/10 dark:ring-white/15 ${className ?? ''}`}
      aria-hidden
    >
      <Flag />
    </span>
  )
}

/** Bosnia and Herzegovina: blue field, yellow diagonal, stars along its edge. */
function BosniaFlag() {
  return (
    <svg viewBox="0 0 20 14" className="size-full" role="presentation">
      <rect width="20" height="14" fill="#002395" />
      {/* Hypotenuse from (5,0) to (17,14) — slope 14/12. */}
      <path d="M5 0 L17 0 L17 14 Z" fill="#FECB00" />
      {/*
        The stars track the hypotenuse at the same slope, held far enough left
        to stay on the blue for their whole run. The first attempt stepped
        1.6 across and 2.0 down — slightly shallower than the edge it was
        following, so the line converged on it and the last two stars ended up
        sitting on the yellow, where a white star on gold is nearly invisible.
        dy is now dx × 14/12 exactly, which keeps the gap constant.

        The real flag clips a star at each end; whole stars are kept here
        because a half star at 20px reads as a rendering fault rather than as
        the flag.
      */}
      <g fill="#fff">
        {Array.from({ length: 6 }, (_, i) => {
          const x = 3.4 + i * 1.6
          const y = 1.3 + i * 1.6 * (14 / 12)

          return <path key={i} d={star(x, y, 1.15)} />
        })}
      </g>
    </svg>
  )
}

/** Croatia: red, white, blue, with the chequy square centred. */
function CroatiaFlag() {
  return (
    <svg viewBox="0 0 20 14" className="size-full" role="presentation">
      <rect width="20" height="4.667" fill="#FF0000" />
      <rect y="4.667" width="20" height="4.666" fill="#fff" />
      <rect y="9.333" width="20" height="4.667" fill="#171796" />
      {/*
        The chequy, reduced to a 3×3 of alternating squares. The real šahovnica
        is 5×5 under a crown; at 20px wide those cells would be a third of a
        pixel each, so the pattern is kept and the count is not — which is what
        actually distinguishes this flag from Serbia's at a glance.

        Sized 6×6 and centred on the flag rather than tucked inside the white
        stripe. Inside it, the emblem's own white cells sat on white and only
        the red ones registered — the chequy read as five loose dots instead of
        a checkerboard. Spanning y=4..10 puts its top row against the red band
        and its bottom row against the blue, so both colours of the pattern have
        something to contrast with. It is also how the real flag sits: the arms
        overlap all three bands.

        Red in the top-left corner, as on the current flag.
      */}
      <g transform="translate(7 4)">
        <rect width="6" height="6" fill="#fff" />
        <g fill="#FF0000">
          <rect width="2" height="2" />
          <rect x="4" width="2" height="2" />
          <rect x="2" y="2" width="2" height="2" />
          <rect y="4" width="2" height="2" />
          <rect x="4" y="4" width="2" height="2" />
        </g>
      </g>
    </svg>
  )
}

/**
 * Serbia: red, blue, white — top to bottom.
 *
 * The band order is the whole difference from Croatia at this size, so the
 * coat of arms is left off rather than drawn as a smudge that only muddies it.
 */
function SerbiaFlag() {
  return (
    <svg viewBox="0 0 20 14" className="size-full" role="presentation">
      <rect width="20" height="4.667" fill="#C6363C" />
      <rect y="4.667" width="20" height="4.666" fill="#0C4076" />
      <rect y="9.333" width="20" height="4.667" fill="#fff" />
    </svg>
  )
}

/**
 * English, shown as the Union Jack.
 *
 * By convention only — the language has no flag, and this one names a country
 * that is not the only one speaking it. The `EN` code beside it is the part
 * that is actually correct.
 */
function EnglishFlag() {
  return (
    <svg viewBox="0 0 20 14" className="size-full" role="presentation">
      <rect width="20" height="14" fill="#012169" />
      {/* Diagonals: white saltire, then the narrower red one over it. */}
      <path d="M0 0 L20 14 M20 0 L0 14" stroke="#fff" strokeWidth="2.8" />
      <path d="M0 0 L20 14 M20 0 L0 14" stroke="#C8102E" strokeWidth="1.2" />
      {/* Cross: white ground, red over it. */}
      <path d="M10 0 V14 M0 7 H20" stroke="#fff" strokeWidth="4.6" />
      <path d="M10 0 V14 M0 7 H20" stroke="#C8102E" strokeWidth="2.6" />
    </svg>
  )
}

const FLAGS: Record<Locale, () => React.ReactElement> = {
  bs: BosniaFlag,
  hr: CroatiaFlag,
  sr: SerbiaFlag,
  en: EnglishFlag,
}

/**
 * A five-pointed star as a path, so the six on the Bosnian flag are one
 * expression rather than six hand-written polygons that can drift apart.
 */
function star(cx: number, cy: number, r: number): string {
  const points: string[] = []

  for (let i = 0; i < 10; i++) {
    // Alternating outer and inner radius is what makes it a star rather than a
    // decagon; the -90° offset puts a point at the top.
    const radius = i % 2 === 0 ? r : r * 0.382
    const angle = (Math.PI / 5) * i - Math.PI / 2

    points.push(`${(cx + radius * Math.cos(angle)).toFixed(2)},${(cy + radius * Math.sin(angle)).toFixed(2)}`)
  }

  return `M${points.join('L')}Z`
}
